import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import moment from 'moment';
import { toast } from 'sonner';
import { InternalMeetingNoteType } from '../../../models/AdminModels';
import { useUserSession } from '../../../models/hooks/appStateHooks';

export interface IAddMeetingNoteRequest {
	/** The account the meeting note is logged against. */
	accountId: string;
	/** The CSM meeting type (an `Api.CsmMeetingTypes` value). */
	meetingType: string;
	/** Meeting status — 'Completed' | 'No showed' | 'Rescheduled'. */
	meetingStatus: string;
	/** Meeting length in minutes (15 / 30 / 45 / 60 / 90 / 120). */
	durationInMinutes: number;
	/** Meeting tags — carries 'Onboarding' when the onboarding box is checked. */
	meetingTags: string[];
	/** The account stage recorded on the note's meeting context. */
	accountStage?: string;
	/** The note body as a raw rich-text content state (produced from the editor's HTML). */
	content: Api.IRawRichTextContentState;
	/** Action items to attach to the note (persisted inline on `note.actionItems`). */
	actionItems?: Api.IActionItem[];
	/** Referenced entities (e.g. CC'd employees on `users`); the server emails referenced users. */
	referencedEntities?: Api.IRichContentReferencedEntities;
	/**
	 * When set, the account stage is persisted after the meeting note is saved via the legacy
	 * stage PUT. Pass it only when the stage actually changed so unchanged stages don't round-trip.
	 */
	updatedStage?: string;
	/**
	 * When set (a `Date` or parseable date string), the account's next meeting is persisted after the
	 * note is saved via the legacy PUT `admin/account/{id}/additionalInfo/nextMeeting`. Pass it only
	 * when the next meeting actually changed.
	 */
	updatedNextMeeting?: Date | string;
}

/**
 * Logs a CSM meeting note via `POST admin/account/{id}/meeting` (the react-query replacement for the
 * MobX `AccountOperationsViewModel.addAccountMeetingNote`) and returns the saved `Api.INote`. The
 * request mirrors the legacy shape: top-level meeting metadata plus a `note` carrying the content and
 * an `IMeetingContext`. When `updatedStage` is set, the account stage is persisted afterwards via
 * `PUT admin/account/{id}/additionalInfo/stage` — matching the legacy composer's two-step save.
 *
 * The caller invalidates the account + CSM note queries on success. Mutations have no MutationCache,
 * so the error toast is wired here explicitly.
 */
export function useAddMeetingNoteMutation() {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: async ({
			accountId,
			meetingType,
			meetingStatus,
			durationInMinutes,
			meetingTags,
			accountStage,
			content,
			actionItems,
			referencedEntities,
			updatedStage,
			updatedNextMeeting,
		}: IAddMeetingNoteRequest) => {
			const note: Api.INote = {
				content,
				// Legacy parity: the admin composer always saves meeting notes as public and always
				// carries the three referenced-entity arrays (CC'd employees ride on `users`).
				visibility: 'all',
				actionItems,
				referencedEntities: referencedEntities ?? { companies: [], contacts: [], users: [] },
				context: {
					source: Api.RichContentContextSource.Meeting,
					meetingType: meetingType as Api.CsmMeetingTypes,
					meetingStatus,
					durationInMinutes,
					meetingTags,
					accountStage,
				},
			};

			const saved = await userSession.webServiceHelper.callAsync<Api.INote>(
				`admin/account/${accountId}/meeting`,
				'POST',
				{
					durationInMinutes,
					meetingStatus,
					meetingTags,
					meetingType,
					metWith: InternalMeetingNoteType.CSM,
					note,
				}
			);

			if (updatedStage) {
				await userSession.webServiceHelper.callAsync<Api.IAccount>(
					`admin/account/${accountId}/additionalInfo/stage?stage=${encodeURIComponent(updatedStage)}`,
					'PUT'
				);
			}

			if (updatedNextMeeting) {
				await userSession.webServiceHelper.callAsync<Api.IAccount>(
					`admin/account/${accountId}/additionalInfo/nextMeeting?nextMeeting=${encodeURIComponent(
						moment(updatedNextMeeting).format('MM/DD/YYYY')
					)}`,
					'PUT'
				);
			}

			return saved;
		},
		onError: () => toast.error('Failed to save the meeting note. Please try again.'),
	});
}
